/* eslint-disable @typescript-eslint/no-explicit-any */
import { createFileRoute } from "@tanstack/react-router";
import { useAuth, useVenueDetailsBySlug, useI18N } from "common/hooks";
import { useEffect, useMemo, useState } from "react";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { defaultRegion } from "common/config";
import Loader from "@/components/common/Loader";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { Helmet } from "react-helmet";
import { CanonicalTag } from "@/components/common/CanonicalTag";
import { Storage } from "common/storage";
import AnalyticsService from "@/services/analytics";
import AccessibilityGuideContent from "../components/VenueDetailComplex/AccessibilityGuideContent";
import AccessibilityDetailView from "@/components/VenueDetailBasic/AccessibilityDetailView";
import { cn, getVenueSlugs, getStoredPasswordToken, getStoredSsoToken } from "@/lib/utils";

export type AccessibilityGuideSpaceRouteParams = {
  venueId: string;
  spaceId: string;
};

export type AccessibilityGuideSpaceRouteSearch = {
  source?: "map" | "activity";
  showAuthModal?: boolean;
  cid?: string;
  tid?: string;
};

export const Route = createFileRoute("/accessibility-guide-space/$venueId/$spaceId")({
  component: AccessibilityGuideSpace,
  validateSearch: (
    search: Record<string, unknown>,
  ): AccessibilityGuideSpaceRouteSearch => {
    return {
      source: search.source as "map" | "activity",
      showAuthModal: search.showAuthModal as boolean,
      cid: search.cid as string,
      tid: search.tid as string,
    };
  },
  parseParams: (params: Record<string, string>) => {
    return {
      venueId: params.venueId,
      spaceId: params.spaceId,
    };
  },
});

function AccessibilityGuideSpace() {
  const { venueId: venueSlug, spaceId } = Route.useParams();
  const { showAuthModal, cid, tid } = Route.useSearch();

  const i18n = useI18N();
  const { user, isLoading, isFetching } = useAuth();
  const isAuthLoading = isLoading || isFetching;

  const [showAuthModalState, setShowAuthModalState] = useState(showAuthModal);
  const { location } = useGeoLocation();

  // load this venue from the server
  const {
    data,
    error,
    refetch: loadVenueDetails,
    isLoading: isDetailsLoading,
  } = useVenueDetailsBySlug({
    slug: venueSlug,
    latitude:
      location?.latitude.toString() || defaultRegion.latitude.toString(),
    longitude:
      location?.longitude.toString() || defaultRegion.longitude.toString(),
    accessToken: Storage.getCurrentUserToken(),
    enabled: location ? true : false,
    type: "Complex",
    passwordToken: getStoredPasswordToken(venueSlug),
    ssoToken: getStoredSsoToken(venueSlug),
  });

  const { showModal } = useAuthModals();

  // Determine if this is a complex venue
  // Use templateId > 1 as the indicator (same logic as used throughout the app)
  // templateType can be empty string for some complex venues
  const isComplexVenue = useMemo(() => {
    return (
      data?.details?.business?.templateId != null &&
      data?.details?.business?.templateId > 1
    );
  }, [data?.details?.business?.templateId]);

  useEffect(() => {
    if (showAuthModalState && !user && !isAuthLoading) {
      showModal(AuthModalScreen.EnterEmail);
      setShowAuthModalState(false);
    }
  }, [showAuthModalState, user, isAuthLoading, showModal]);

  useEffect(() => {
    if (data?.details) {
      // Track accessibility space guide view with GA
      AnalyticsService.trackGAEventHandler("accessibilitySpaceGuideViewed");

      // Track accessibility space guide view with Mixpanel
      AnalyticsService.trackMx("accessibilitySpaceGuideViewed", {
        VenueId: data.details.business.id,
        VenueName: data.details.name,
        VenueType: data.details.businessType,
        TemplateType: isComplexVenue ? "Complex" : "Basic",
        SpaceId: spaceId,
        Source: "accessibility_guide",
        CampaignId: cid,
        TrackingId: tid,
      });
    }
  }, [data?.details, isComplexVenue, spaceId, cid, tid]);

  return (
    <main id="main" className={cn(
      "bg-bg-brand-secondary-default",
      !isComplexVenue && "max-w-[1440px] mx-auto h-below-navbar overflow-hidden"
    )}>
      <Helmet>
        <title>
          {data?.details?.name
            ? `${data.details.name.length > 30 ? data.details.name.substring(0, 30) + "..." : data.details.name} ${i18n.get("venuePageTitleSuffix")}`
            : i18n.get("venuePageTitleSuffix")}
        </title>
        {data?.details?.name && (
          <meta
            name="description"
            content={`${i18n.get("venueMetaDescriptionPrefix")} ${data.details.name} ${i18n.get("venueMetaDescriptionSuffix")}`}
          />
        )}
      </Helmet>
      <CanonicalTag />
      {(isAuthLoading || isDetailsLoading) && <Loader />}
      {error && (
        <div className="max-w-[1160px] px-4 mx-auto overflow-hidden">
          <div role="alert">{error.message}</div>
        </div>
      )}

      {/* Complex venue */}
      {data && isComplexVenue && (
        <section className="mb-2">
          <AccessibilityGuideContent
            venueDetailsResponse={data}
            venueSlug={venueSlug}
            onClose={() => {
              // Track back navigation
              AnalyticsService.trackGAEventHandler("accessibilitySpaceGuideBackClicked");
              AnalyticsService.trackMx("accessibilitySpaceGuideBackClicked", {
                VenueId: data.details.business.id,
                VenueName: data.details.name,
                SpaceId: spaceId,
              });
              
              const { venueDetailsSlug } = getVenueSlugs(data.details);
              window.location.href = `/venuedetails/${venueDetailsSlug}`;
            }}
            onRefresh={() => {
              setTimeout(() => loadVenueDetails(), 1000);
            }}
            cid={cid}
            tid={tid}
            initialSpaceId={spaceId}
            mode="accessibility-guide-space"
          />
        </section>
      )}

      {/* Basic venue: viewport-locked shell — mirrors venuedetails.$venueId.tsx */}
      {data && !isComplexVenue && (
        <section className="h-below-navbar bg-bg-brand-secondary-default flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-hidden">
            <AccessibilityDetailView
              venueDetails={data.details}
              accessibilityCategories={data.accesibilityCategories || []}
              initialSpaceId={spaceId}
              pageName="Accessibility Space Guide"
              onClose={() => {
                // Track back navigation
                AnalyticsService.trackGAEventHandler("accessibilitySpaceGuideBackClicked");
                AnalyticsService.trackMx("accessibilitySpaceGuideBackClicked", {
                  VenueId: data.details.business.id,
                  VenueName: data.details.name,
                  SpaceId: spaceId,
                });

                const { venueDetailsSlug } = getVenueSlugs(data.details);
                window.location.href = `/venuedetails/${venueDetailsSlug}`;
              }}
              onShare={(currentSpaceId) => {
                // Track share action
                AnalyticsService.trackGAEventHandler("accessibilitySpaceGuideShared");
                AnalyticsService.trackMx("accessibilitySpaceGuideShared", {
                  VenueId: data.details.business.id,
                  VenueName: data.details.name,
                  SpaceId: currentSpaceId,
                });

                if (navigator.share) {
                  navigator.share({
                    title: data.details.name,
                    text: `${i18n.get("checkOutAccessibilityInfoAt")} ${data.details.name}`,
                    url: `${window.location.origin}/accessibility-guide-space/${data.details.business.slug}/${currentSpaceId}`,
                  });
                }
              }}
            />
          </div>
        </section>
      )}
    </main>
  );
}
